import { useCallback, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { TreeView } from 'devextreme-react/tree-view';

function buildTreeData(categories, subcategories) {
  const grouped = subcategories.reduce((map, s) => {
    if (!map.has(s.CategoryId)) map.set(s.CategoryId, []);
    map.get(s.CategoryId).push(s);
    return map;
  }, new Map());

  const items = [];
  for (const c of categories) {
    items.push({ id: `c_${c.Value}`, parentId: null, text: c.Text, categoryId: c.Value, expanded: true });
    for (const s of grouped.get(c.Value) || []) {
      items.push({ id: `s_${s.Value}`, parentId: `c_${c.Value}`, text: s.Text, subCategoryId: s.Value });
    }
  }
  return items;
}

export function CategorySelectorContent({ categories = [], subcategories = [], onSelectionChanged, selectedCategories = [] }) {
  const guard = useRef(false);
  const treeViewRef = useRef(null);
  const initApplied = useRef(false);

  const treeData = useMemo(
    () => buildTreeData(categories, subcategories),
    [categories, subcategories]
  );

  const initialKeys = useMemo(() =>
    selectedCategories.flatMap(s => {
      const catId = s.CategoryId ?? s.categoryId;
      const subCatId = s.SubCategoryId ?? s.subCategoryId;
      return subCatId ? [`c_${catId}`, `s_${subCatId}`] : [`c_${catId}`];
    }),
    [selectedCategories]
  );

  const handleContentReady = useCallback((e) => {
    if (initApplied.current || initialKeys.length === 0) return;
    initApplied.current = true;
    guard.current = true;
    initialKeys.forEach(key => e.component.selectItem(key));
    guard.current = false;
  }, [initialKeys]);

  const handleItemSelectionChanged = useCallback((e) => {
    if (guard.current) return;

    guard.current = true;
    if (!e.node?.parent) {
      if (!e.node.selected) {
        (e.node.children ?? []).forEach(child => e.component.unselectItem(child.key));
      }
    } else {
      if (e.node.selected) {
        e.component.getSelectedNodes()
          .filter(n => n.parent?.key === e.node.parent.key && n.key !== e.node.key)
          .forEach(n => e.component.unselectItem(n.key));
        e.component.selectItem(e.node.parent.key);
      } else {
        const hasSelectedChild = e.component.getSelectedNodes()
          .some(n => n.parent?.key === e.node.parent.key);
        if (!hasSelectedChild)
          e.component.unselectItem(e.node.parent.key);
      }
    }
    guard.current = false;
  }, []);

  const handleSelectionChanged = useCallback((e) => {
    if (guard.current) return;

    const selectedNodes = e.component.getSelectedNodes();
    const result = selectedNodes
      .filter(n => {
        if (n.parent) return true;
        return !selectedNodes.some(c => c.parent?.key === n.key);
      })
      .map(n => {
        if (!n.parent) {
          return { categoryId: n.itemData.categoryId, categoryText: n.itemData.text };
        }
        return {
          categoryId: n.parent.itemData.categoryId,
          categoryText: n.parent.itemData.text,
          subCategoryId: n.itemData.subCategoryId,
          subCategoryText: n.itemData.text,
          id: n.itemData.Id,
        };
      });

    onSelectionChanged?.(result);
  }, [onSelectionChanged]);

  return (
    <TreeView
      ref={treeViewRef}
      dataSource={treeData}
      dataStructure="plain"
      keyExpr="id"
      parentIdExpr="parentId"
      displayExpr="text"
      selectionMode="multiple"
      showCheckBoxesMode="normal"
      selectNodesRecursive={false}
      onContentReady={handleContentReady}
      onItemSelectionChanged={handleItemSelectionChanged}
      onSelectionChanged={handleSelectionChanged}
    />
  );
}

CategorySelectorContent.propTypes = {
  categories: PropTypes.array,
  subcategories: PropTypes.array,
  onSelectionChanged: PropTypes.func,
  selectedCategories: PropTypes.array,
};
